import { Inject, Injectable } from '@angular/core';
import { APP_STORE_CONFIG_TOKEN, ERROR_CODES, NilePaConstants } from '@ddt-web/config';
import { ConfigService, PcConfigKey } from '@ddt-web/config-layer';
import { AppStoreConfig, DataServiceApiConstants, PasRcResolverService } from '@ddt-web/data-service';
import { ErrorObject, PasSecurityDetailsPartialError, SimpleErrorObject, WweErrorPortfolioDetails } from '@ddt-web/error-handler';
import { AssetResolveResponse, PasApiService } from '@ddt-web/pas';
import { StoreService } from '@ddt-web/store-service';
import { SecurityIdentifiersMapRequest, WweSecurityDetailsParams } from '@ddt-web/wealth-web';
import * as isEmpty from 'lodash/isEmpty';
import { first, map, switchMap } from 'rxjs/operators';

import { AbstractWorker } from '../models/abstract-worker';

/**
 * Makes a multi-resolve API call to Wealth Web APIs from a list of indexes from config.
 */
@Injectable()
export class NileIndexesLoader extends AbstractWorker {
	constructor(
		private storeService: StoreService,
		private pasApiService: PasApiService,
		private configService: ConfigService,
		@Inject(APP_STORE_CONFIG_TOKEN) public storeConfig: AppStoreConfig,
		private pasRcResolver: PasRcResolverService
	) {
		super();
	}

	public handle(): void {
		const rawIndexList = this.configService.get(PcConfigKey.PC_BENCHMARK_SELECTION_OPTIONS, null);
		let listOfIndexes;

		if (!isEmpty(rawIndexList)) {
			listOfIndexes = rawIndexList.split(',');
		} else {
			throw new SimpleErrorObject(ERROR_CODES.BENCHMARK_LIST_ERROR);
		}

		const body = {
			securities: listOfIndexes.map((id, i) => {
				return {
					id: i.toString(),
					identifiers: {
						aladdinId: id
					}
				} as SecurityIdentifiersMapRequest;
			})
		};

		this.pasRcResolver.getRcForUserType(DataServiceApiConstants.SECURITY_DETAILS).pipe(
			map((rc: string) => ({ body, rc })),
			switchMap((requestParams: WweSecurityDetailsParams) => this.pasApiService.fetchSecurityDetails(requestParams)),
			first()
		).subscribe((response: AssetResolveResponse) => {
			if (isEmpty(response.assets)) {
				this.handleIndexSecurityDetailsError(response.errorCodes);
			}

			const children = Array.from(response.assets.values());

			const payload = {
				id: NilePaConstants.BENCHMARKS,
				children: children
			};
			this.storeService.upsertOne(this.storeConfig.positionDetails, payload);
		});
	}

	public handleIndexSecurityDetailsError(errorCodes): void {
		const configName = this.storeConfig.positionDetails;
		this.storeService.upsertMany(configName, []);

		const errorPortfolioDetails = { id: 'LIST_OF_BENCHMARKS' } as WweErrorPortfolioDetails;
		throw new ErrorObject(new PasSecurityDetailsPartialError(errorCodes, [errorPortfolioDetails]), configName.name);
	}
}
